import { supabase } from "./supabase";

// Manda a senha para as 10 escolas do Memberkit, pela Edge Function
// `sincronizar-senha`. Requisito do recadastro: sem isto o aluno passa a ter
// duas senhas, uma no hub e outra nas aulas.
//
// Chamado nos momentos em que a senha em texto existe no navegador: ao entrar,
// ao redefinir, e no pedido explícito do banner `LiberarAulas` (que existe
// porque quem confirma o e-mail pelo link cai logado sem passar pelo login).
// Em nenhum outro lugar ela está disponível — nem no backend, que não a guarda.
export type ResultadoSincronizacao = "ok" | "falhou" | "senha-recusada" | "sem-sessao";

export async function sincronizarSenhaComMemberkit(
  senha: string,
  motivo: "login" | "redefinicao",
): Promise<ResultadoSincronizacao> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return "sem-sessao";

    // O retorno É conferido, ao contrário da primeira versão: `invoke` não
    // lança em erro de rede nem em status 4xx/5xx — devolve `{ error }`. Sem
    // olhar, uma falha de CORS ou a function fora do ar viravam sucesso
    // silencioso, e quem chama não tinha como saber (revisão de 12/08).
    const { data: resposta, error } = await supabase.functions.invoke(
      `sincronizar-senha?motivo=${motivo}`,
      { body: { senha }, headers: { Authorization: `Bearer ${token}` } },
    );

    if (error) return "falhou";
    // 400 da function = senha ausente ou curta. Vale distinguir: é o único caso
    // em que insistir com a mesma senha não vai funcionar.
    if (resposta && typeof resposta === "object" && "erro" in resposta) {
      return "senha-recusada";
    }
    // `sincronizado: false` = alguma escola falhou. O carimbo fica nulo e o
    // próximo acesso reempurra.
    if (resposta && typeof resposta === "object" && resposta.sincronizado === false) {
      return "falhou";
    }
    return "ok";
  } catch {
    return "falhou";
  }
}

// Quem está pendente? `mk_senha_em` nulo é o estado de nascença dos 58 mil
// perfis da carga, o que a Edge Function deixa depois de uma falha, e o que o
// gatilho da 000036 escreve sempre que a senha muda no Auth.
//
// Sem esta checagem, todo login de todo aluno gastaria 10 chamadas ao Memberkit
// — e o limite deles é 120 por minuto na conta inteira, que uma onda de
// recadastro estouraria sozinha.
//
// Devolve `null` quando não deu para saber (erro de leitura, perfil ainda não
// vinculado). Nesse caso não se sincroniza e não se pede senha ao aluno: não
// afirmar nada é melhor que afirmar errado nas duas direções.
export async function sincronizacaoPendente(): Promise<boolean | null> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("mk_senha_em")
      .maybeSingle();
    if (error || !data) return null;
    return data.mk_senha_em === null;
  } catch {
    return null;
  }
}

// No login: só sincroniza se estiver pendente. Sem await no chamador, porque
// **nunca bloqueia o aluno** — se o Memberkit estiver fora do ar, a entrada
// continua e o carimbo segue nulo, que o próximo acesso resolve. Travar a porta
// do hub por um sistema terceiro seria trocar um problema pequeno (senha
// diferente nas aulas por algumas horas) por um grande (aluno sem acesso a nada).
export async function sincronizarSePendente(senha: string): Promise<void> {
  if (await sincronizacaoPendente()) {
    await sincronizarSenhaComMemberkit(senha, "login");
  }
}
